import { z } from "zod";

export const QuoteStatusSchema = z.enum([
  "draft",
  "sent",
  "viewed",
  "accepted",
  "expired",
  "declined",
]);
export type QuoteStatus = z.infer<typeof QuoteStatusSchema>;

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  viewed: "Viewed",
  accepted: "Accepted",
  expired: "Expired",
  declined: "Declined",
};

// Badge variant per status — mirrors the Badge component's variant union.
export const QUOTE_STATUS_BADGE_VARIANT: Record<
  QuoteStatus,
  "default" | "success" | "warning" | "danger" | "info" | "secondary"
> = {
  draft: "secondary",
  sent: "info",
  viewed: "warning",
  accepted: "success",
  expired: "default",
  declined: "danger",
};

export const LineItemSchema = z.object({
  label: z.string(),
  qty: z.number(),
  unit_cents: z.number(),
  amount_cents: z.number(),
});
export type LineItem = z.infer<typeof LineItemSchema>;

export const QuoteSchema = z.object({
  id: z.string(),
  inquiry_id: z.string(),
  package_id: z.string(),
  line_items: z.array(LineItemSchema),
  subtotal_cents: z.number(),
  service_charge_pct: z.number(),
  service_charge_cents: z.number(),
  total_cents: z.number(),
  deposit_pct: z.number(),
  deposit_cents: z.number(),
  status: QuoteStatusSchema,
  sent_at: z.string().nullable(),
  viewed_at: z.string().nullable(),
  accepted_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  pdf_key: z.string().nullable(),
  created_at: z.string(),
});
export type Quote = z.infer<typeof QuoteSchema>;

export const QuoteListResponseSchema = z.object({
  items: z.array(QuoteSchema),
});
export type QuoteListResponse = z.infer<typeof QuoteListResponseSchema>;

// ---- Forms / mutation inputs ------------------------------------------

// A single editable row in the quote builder's line-item table. Unit price
// is authored in dollars (a plain string, so the input can hold "" and
// partial values like "1,350" while typing) and converted to cents only at
// submit time — never in a live-preview money computation stored back to
// the server.
export interface LineItemDraft {
  key: string;
  label: string;
  qty: string;
  unitDollars: string;
}

export function emptyLineItemDraft(key: string): LineItemDraft {
  return { key, label: "", qty: "1", unitDollars: "" };
}

export interface QuoteLineItemInput {
  label: string;
  qty: number;
  unit_cents: number;
  amount_cents: number;
}

export interface CreateQuoteInput {
  inquiry_id: string;
  package_id: string;
  line_items?: QuoteLineItemInput[];
  service_charge_pct?: number;
  deposit_pct?: number;
}

export interface PatchQuoteInput {
  id: string;
  line_items?: QuoteLineItemInput[];
  service_charge_pct?: number;
  deposit_pct?: number;
}
